// src/components/SpreadsheetCellViewer.jsx
// A captured exam spreadsheet answer (see submissions.js's spreadsheetAnswers
// / hasInteractiveSpreadsheet, gated by the head marker's per-homework-type
// "Interactive Spreadsheet Marking" setting), shown in FirebaseMarker.jsx's
// page area in place of a PDF page and marked with the same toolbar, clicks,
// right-click tool menu, comment popup and highlighter colours as the PDF.
// Renders an actual grid from gridJson - the same shape ExamSpreadsheetGrid.jsx
// produces - with a formula bar showing whichever cell the mouse is over.
//
// This component only draws and reports clicks: FirebaseMarker.jsx decides
// what a click on a cell does for the active tool (handleCellClick). Items
// placed on a cell live in FirebaseMarker's normal marks / numberedFeedback /
// professionalMarks / annotations / textStamps arrays, tagged with
// `cell: { groupId, gridKey, row, col }` and `page: null`, so they count
// towards the same totals as marks on the PDF. The marked PDF draws them on
// separate pages (utils/markedSpreadsheetPages.js). The student's cells are
// never changed.
//
// `readOnly` is the student's view of their marked spreadsheet
// (StudentSpreadsheetView.jsx): the same grid and marking, nothing to click.
//
// Markers can widen or narrow a column (drag the edge of its letter, or
// double-click the edge to fit the longest entry) to read what's in it -
// `colWidthOverrides` / onColWidthsChange, saved with the marking as
// spreadsheetColWidths and used by the marked PDF too. Text runs on into
// empty cells to its right, as in a spreadsheet.

import React, { useMemo, useState, useEffect } from 'react';
import { parseGrid, colLetter, cellBorderCss, effectiveAlign } from './ExamSpreadsheetGrid';
import { evaluateGrid } from '../utils/spreadsheetFormulas';

const ROW_HEADER_WIDTH = 40;
const ROW_HEIGHT = 30;

const headerCellStyle = (width) => ({
  width,
  minWidth: width,
  background: '#f8f9fa',
  border: '1px solid #e2e8f0',
  fontSize: '11px',
  fontWeight: 'normal',
  color: '#4a5568',
  padding: '2px',
  position: 'sticky',
  top: 0,
  zIndex: 2
});

// Same look as the marks on a PDF page
const circleStyle = (background, size, surplus) => ({
  width: size, height: size, borderRadius: '50%',
  background: surplus ? '#95a5a6' : background, color: 'white',
  fontWeight: 'bold', fontSize: size > 20 ? '10px' : '11px',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  border: surplus ? '2px dashed #7f8c8d' : '2px solid white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)', flexShrink: 0,
  opacity: surplus ? 0.6 : 1
});

// Widest entry in a column, for double-click-to-fit
function fitColumnWidth(grid, evaluated, col) {
  const ctx = document.createElement('canvas').getContext('2d');
  const family = getComputedStyle(document.body).fontFamily;
  let widest = 0;
  grid.forEach((row, r) => {
    const text = String(evaluated[r]?.[col]?.display ?? '');
    if (!text) return;
    ctx.font = `${row[col]?.bold ? 'bold ' : ''}13px ${family}`;
    widest = Math.max(widest, ctx.measureText(text).width);
  });
  return Math.min(600, Math.max(40, Math.ceil(widest) + 16));
}

function MarkingGrid({ groupId, gridKey, label, gridJson, precomputedEval, items, activeTool, onCellClick, onHighlightRange, onDeleteProfessionalMark, surplusIds, onHover, colWidthOverrides, onColWidthsChange, zoomLevel, readOnly, gridMaxHeight = '75vh' }) {
  const { grid, colWidths: studentWidths } = useMemo(() => parseGrid(gridJson), [gridJson]);
  const widthsKey = `${groupId}:${gridKey}`;
  const saved = colWidthOverrides?.[widthsKey];
  const [resizing, setResizing] = useState(null); // { col, startX, startWidth, width }
  const colWidths = useMemo(() => {
    const base = Array.isArray(saved) && saved.length === studentWidths.length ? saved : studentWidths;
    return resizing ? base.map((w, i) => (i === resizing.col ? resizing.width : w)) : base;
  }, [saved, studentWidths, resizing]);
  // precomputedEval (uploaded .xlsx submissions - see parseUploadedSpreadsheet.js)
  // carries Excel's own cached cell values instead of this engine's limited
  // re-evaluation - see generateExamPdf.js's renderSpreadsheetGrid for the
  // same reasoning, so the PDF and this view always agree.
  const evaluated = useMemo(() => precomputedEval || evaluateGrid(grid), [grid, precomputedEval]);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);

  // Highlighter drag finished outside the grid - drop it
  useEffect(() => {
    if (!dragStart) return undefined;
    const cancel = () => { setDragStart(null); setDragEnd(null); };
    window.addEventListener('mouseup', cancel);
    return () => window.removeEventListener('mouseup', cancel);
  }, [dragStart]);

  // Dragging a column edge
  useEffect(() => {
    if (!resizing) return undefined;
    const move = (e) => setResizing(prev => prev && { ...prev, width: Math.max(30, Math.round(prev.startWidth + (e.clientX - prev.startX) / (zoomLevel || 1))) });
    // colWidths already has the dragged width in it
    const up = () => {
      onColWidthsChange(widthsKey, colWidths);
      setResizing(null);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [resizing, colWidths, onColWidthsChange, widthsKey, zoomLevel]);

  const here = (cell, r, c) => !!cell && cell.groupId === groupId && cell.gridKey === gridKey && cell.row === r && cell.col === c;
  const inDrag = (r, c) => dragStart && dragEnd &&
    r >= Math.min(dragStart.row, dragEnd.row) && r <= Math.max(dragStart.row, dragEnd.row) &&
    c >= Math.min(dragStart.col, dragEnd.col) && c <= Math.max(dragStart.col, dragEnd.col);
  const cellRef = (r, c) => ({ groupId, gridKey, row: r, col: c });

  return (
    <div style={{ marginBottom: '20px' }}>
      {label && <div style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50', marginBottom: '8px' }}>{label}</div>}
      <div style={{ overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px', maxHeight: gridMaxHeight }}>
        <table style={{ borderCollapse: 'collapse', fontSize: '13px', userSelect: 'none' }}>
          <thead>
            <tr>
              <th style={headerCellStyle(ROW_HEADER_WIDTH)}></th>
              {grid[0]?.map((_, ci) => (
                <th key={ci} style={headerCellStyle(colWidths[ci] || 90)}>
                  {colLetter(ci)}
                  {!readOnly && <div
                    title="Drag to resize, double-click to fit"
                    onMouseDown={(e) => {
                      if (e.button !== 0) return;
                      e.preventDefault();
                      e.stopPropagation();
                      setResizing({ col: ci, startX: e.clientX, startWidth: colWidths[ci] || 90, width: colWidths[ci] || 90 });
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      onColWidthsChange(widthsKey, colWidths.map((w, i) => (i === ci ? fitColumnWidth(grid, evaluated, ci) : w)));
                    }}
                    style={{ position: 'absolute', top: 0, right: -4, width: 8, height: '100%', cursor: 'col-resize', zIndex: 3 }}
                  />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.map((row, ri) => (
              <tr key={ri}>
                <th style={{ ...headerCellStyle(ROW_HEADER_WIDTH), position: 'sticky', left: 0, zIndex: 1 }}>{ri + 1}</th>
                {row.map((cell, ci) => {
                  const cellEval = evaluated[ri]?.[ci];
                  const borderCss = cellBorderCss(cell.borders);
                  const marks = items.marks.filter(m => here(m.cell, ri, ci));
                  const feedback = items.numberedFeedback.filter(f => here(f.cell, ri, ci));
                  const prof = items.professionalMarks.filter(m => here(m.cell, ri, ci));
                  const addlProf = items.additionalProfessionalMarks.filter(m => here(m.cell, ri, ci));
                  const stamps = items.textStamps.filter(s => here(s.cell, ri, ci));
                  const highlight = items.annotations.find(a => a.type === 'highlight' && here(a.cell, ri, ci));
                  const width = colWidths[ci] || 90;
                  // Text (not numbers) runs on into the empty cells to its right
                  let runWidth = width;
                  const text = cellEval?.display;
                  const isNumber = cellEval && !cellEval.isError && cellEval.numeric !== null && cellEval.numeric !== undefined;
                  if (text && !isNumber && effectiveAlign(cell, cellEval) === 'left') {
                    for (let n = ci + 1; n < row.length && !evaluated[ri]?.[n]?.display; n++) runWidth += colWidths[n] || 90;
                  }
                  const runsOn = runWidth > width;
                  return (
                    <td
                      key={ci}
                      onMouseEnter={() => {
                        onHover({ ref: `${colLetter(ci)}${ri + 1}`, formula: cellEval?.isFormula ? cell.value : null, display: cellEval?.display || '' });
                        if (dragStart) setDragEnd({ row: ri, col: ci });
                      }}
                      onMouseDown={(e) => {
                        if (e.button !== 0 || activeTool !== 'highlighter') return;
                        e.stopPropagation();
                        setDragStart({ row: ri, col: ci });
                        setDragEnd({ row: ri, col: ci });
                      }}
                      onMouseUp={(e) => {
                        e.stopPropagation();
                        if (!dragStart) return;
                        const cells = [];
                        for (let r = Math.min(dragStart.row, ri); r <= Math.max(dragStart.row, ri); r++) {
                          for (let c = Math.min(dragStart.col, ci); c <= Math.max(dragStart.col, ci); c++) cells.push(cellRef(r, c));
                        }
                        setDragStart(null);
                        setDragEnd(null);
                        onHighlightRange(cells);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (activeTool !== 'highlighter') onCellClick(cellRef(ri, ci), e);
                      }}
                      style={{
                        width,
                        minWidth: width,
                        maxWidth: width,
                        height: ROW_HEIGHT,
                        verticalAlign: 'top',
                        border: '1px solid #e2e8f0',
                        padding: 0,
                        background: inDrag(ri, ci) ? '#dfe6ff' : highlight ? `${highlight.color}4D` : 'white',
                        position: 'relative'
                      }}
                    >
                      <div style={{
                        padding: '2px 6px',
                        lineHeight: `${ROW_HEIGHT - 6}px`,
                        textAlign: effectiveAlign(cell, cellEval),
                        fontWeight: cell.bold ? 'bold' : 'normal',
                        fontStyle: cell.italic ? 'italic' : 'normal',
                        textDecoration: cell.underline ? 'underline' : 'none',
                        color: cellEval?.isError ? '#c0392b' : cell.fontColor || 'inherit',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden'
                      }}>
                        {runsOn ? '\u00a0' : text}
                      </div>
                      {runsOn && (
                        <div style={{
                          position: 'absolute', left: 0, top: 0, width: runWidth, boxSizing: 'border-box',
                          padding: '2px 6px', lineHeight: `${ROW_HEIGHT - 6}px`, whiteSpace: 'nowrap', overflow: 'hidden',
                          fontWeight: cell.bold ? 'bold' : 'normal',
                          fontStyle: cell.italic ? 'italic' : 'normal',
                          textDecoration: cell.underline ? 'underline' : 'none',
                          color: cell.fontColor || 'inherit',
                          pointerEvents: 'none', zIndex: 1
                        }}>
                          {text}
                        </div>
                      )}
                      {borderCss && <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', ...borderCss }} />}
                      {(feedback.length > 0 || marks.length > 0 || prof.length > 0 || addlProf.length > 0 || stamps.length > 0) && (
                        // In a strip under the cell's own figure (the row grows to
                        // make room), so marking never covers what the student typed
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: '2px', padding: '0 4px 3px' }}>
                          {feedback.map(f => (
                            <span key={f.id} title={f.text || undefined} style={{ color: '#e74c3c', fontWeight: 'bold', fontSize: '14px', lineHeight: '20px', padding: '0 2px', pointerEvents: 'none' }}>
                              {f.number}
                            </span>
                          ))}
                          {marks.map(m => (
                            <div key={m.id} title={surplusIds.has(m.id) ? 'Not counted (over max)' : undefined} style={{ ...circleStyle(m.type === 'full-technical' ? '#1b998b' : '#445e93', 20, surplusIds.has(m.id)), pointerEvents: 'none' }}>
                              {m.type === 'full-technical' ? '1' : '½'}
                            </div>
                          ))}
                          {prof.map(m => (
                            <div key={m.id} title={readOnly ? m.reason || 'Professional mark' : `${m.reason || 'Professional mark'} (double-click to remove)`}
                              onDoubleClick={(e) => { e.stopPropagation(); if (!readOnly) onDeleteProfessionalMark(m, false); }}
                              style={{ ...circleStyle('#f39c12', 20, false), cursor: readOnly ? 'default' : 'pointer' }}>
                              P{m.markNumber || '?'}
                            </div>
                          ))}
                          {addlProf.map(m => (
                            <div key={m.id} title={readOnly ? m.reason || 'Additional professional mark' : `${m.reason || 'Additional professional mark'} (double-click to remove)`}
                              onDoubleClick={(e) => { e.stopPropagation(); if (!readOnly) onDeleteProfessionalMark(m, true); }}
                              style={{ ...circleStyle('#7b2d8b', 20, false), cursor: readOnly ? 'default' : 'pointer' }}>
                              P{m.markNumber || '?'}
                            </div>
                          ))}
                          {stamps.map(s => (
                            <div key={s.id} style={{ padding: '0 3px', border: '1.5px solid #1a1a2e', background: 'white', color: '#1a1a2e', fontWeight: 'bold', fontSize: '11px', lineHeight: '16px', pointerEvents: 'none' }}>
                              {s.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SpreadsheetCellViewer({ groupId, gridKey = 'spreadsheet', answer, zoomLevel = 1, fluid = false, ...rest }) {
  const [hover, setHover] = useState(null);
  const precomputedEval = useMemo(() => {
    try { return answer.precomputedEvalJson ? JSON.parse(answer.precomputedEvalJson) : undefined; } catch (e) { return undefined; }
  }, [answer.precomputedEvalJson]);

  return (
    <div style={fluid ? { width: '100%' } : { zoom: zoomLevel, width: `${800 / zoomLevel}px`, maxWidth: `${800 / zoomLevel}px` }}>
      {/* Formula bar - whichever cell the mouse is over */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px',
        border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 10px',
        fontSize: '13px', minHeight: '20px', background: '#fafbfc'
      }}>
        <strong style={{ minWidth: '40px', color: '#2c3e50' }}>{hover?.ref || ''}</strong>
        <span style={{ fontFamily: 'monospace', color: '#4a5568', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {hover ? (hover.formula || hover.display) : 'Point at a cell to see its formula'}
        </span>
        {hover?.formula && <span style={{ color: '#2c3e50', fontWeight: '600' }}>= {hover.display}</span>}
      </div>
      {gridKey === 'spreadsheet' && answer.gridJson && (
        <MarkingGrid
          {...rest}
          zoomLevel={zoomLevel}
          groupId={groupId}
          gridKey="spreadsheet"
          label={[answer.number && `Spreadsheet ${answer.number}`, answer.blankGridJson && 'Prepopulated Spreadsheet'].filter(Boolean).join(': ') || null}
          gridJson={answer.gridJson}
          precomputedEval={precomputedEval}
          onHover={setHover}
        />
      )}
      {gridKey === 'blankSpreadsheet' && answer.blankGridJson && (
        <MarkingGrid
          {...rest}
          zoomLevel={zoomLevel}
          groupId={groupId}
          gridKey="blankSpreadsheet"
          label={[answer.blankNumber && `Spreadsheet ${answer.blankNumber}`, 'Blank Spreadsheet'].filter(Boolean).join(': ')}
          gridJson={answer.blankGridJson}
          onHover={setHover}
        />
      )}
    </div>
  );
}
